import { Button } from '@Pages/pageRuntime/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@Pages/pageRuntime/components/ui/dialog/Dialog';
import { RecurrenceScopeInput } from '@Scheduling/components/RecurrenceScopeInput';
import { recurrenceScopeLabel } from '@Scheduling/components/recurrenceScopeLabel';
import { useState } from 'react';

import {
  RecurringEditScope,
  type RecurringEditScope as RecurringEditScopeValue
} from '@/types/enums';

import {
  isSeriesOccurrence,
  recurrenceChanged,
  type RecordUpdateInteractionPromptProps
} from './recordUpdateInteractions';

const repeatChangeScopes = {
  [RecurringEditScope.This]: `Changes to how it repeats apply to "${recurrenceScopeLabel(RecurringEditScope.ThisAndFuture)}" or "${recurrenceScopeLabel(RecurringEditScope.All)}".`
};

/**
 * Pre-submit prompt for the `eventable` capability: asks how much of a recurring series
 * an update should affect and resolves the interaction with `{ eventable: { scope } }`.
 * The series engine refuses a change to how it repeats on a single occurrence, so that
 * option is disabled when the form changed the repeat of a series occurrence.
 */
export function RecurringEventableScopePrompt({
  resource,
  context,
  isSubmitting,
  onResolve,
  onCancel
}: RecordUpdateInteractionPromptProps) {
  const [repeatChanged] = useState(
    () =>
      isSeriesOccurrence(context.record) &&
      recurrenceChanged(context.resource, context.record, context.data)
  );
  const [scope, setScope] = useState<RecurringEditScopeValue>(
    repeatChanged ? RecurringEditScope.ThisAndFuture : RecurringEditScope.This
  );
  const noun = resource.singular_name.toLowerCase();

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onCancel())}>
      <DialogContent closeable={!isSubmitting} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Update recurring {noun}</DialogTitle>
          <DialogDescription>
            Choose how much of this recurring series should be changed.
          </DialogDescription>
        </DialogHeader>
        <RecurrenceScopeInput
          value={scope}
          onChange={setScope}
          disabledScopes={repeatChanged ? repeatChangeScopes : undefined}
          disabled={isSubmitting}
        />
        <DialogFooter
          start={
            <Button type="button" variant="ghost" disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          }
        >
          <Button
            type="button"
            loading={isSubmitting}
            onClick={() => onResolve({ eventable: { scope } })}
          >
            Update {noun}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
